import React, { useContext, useMemo, useEffect, useCallback } from 'react';
import GamUprOsConfig from './GamUprOsConfig';
import { ConfigContext } from '../../../../../Contexts/ConfigContext';
import CheckboxInput from '../../../../../Components/CheckBoxInput';
import constants from '../constants.json';
import PageHeading from '../../../../../Components/PageHeading';
//mui imports====
import { Box, Divider, Paper, Grid } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabPanel from '@mui/lab/TabPanel';
import useCommonStyles from '../../../../../commonStyles';
import GeneralTab from '../../../../../Components/GeneralTabAndTabContent';
import SwitchInput from '../../../../../Components/SwitchInput';

import { UnitsContext } from '../../../../../Contexts/UnitsContext';
import { CONFIG_KEYDATA_CONSTANTS } from '../../../../../core/constants';
import { FEATURE_DESCRIPTION } from '../../../constant';
import { GAM_UPR_PLATFORMS } from '../../../../../core/constants/gamUprConstants';
import { FLOOR_PRICE_MODERATOR_OPTIMIZED_CONFIG } from '../../../../../core/constants';
import { initialRawUnitConfig } from '../../../../../utilities/initialConfigs';
import FloorAutomationToggle from './FloorAutomationToggle';

const { FP_ENGINE_ENABLED_KEY, FP_ENGINE_EXPERIMENT_KEY, FP_ENGINE_CONFIGS_KEY } =
	CONFIG_KEYDATA_CONSTANTS;

const GamUprPlatformConfig = ({ keyData, featureDescription }) => {
	const commonStyleClasses = useCommonStyles();
	const {
		gamUprPlatforms: { DEFAULT, DESKTOP, MOBILE, TABLET }
	} = constants;
	const { rawConfig, updateRawConfig } = useContext(ConfigContext);
	const { siteRawConfig, updateSiteRawConfig, units } = useContext(UnitsContext);

	const gamUprFloorAutomationEnabled = !!rawConfig[keyData]?.gamUprFloorAutomation?.enabled;

	const enableGamUpr = (enabledValue) => {
		if (rawConfig && keyData) {
			// eslint-disable-next-line prefer-const
			let updatedObject = structuredClone(rawConfig);
			if (!updatedObject[keyData]) {
				updatedObject[keyData] = {};
			}
			updatedObject[keyData].enabled = enabledValue;
			updateRawConfig(updatedObject);
		} else {
			alert('Raw Config  is missing');
		}
	};

	useEffect(() => {
		if (rawConfig[keyData]?.gamUprFloorAutomation?.enabled === false) {
			const clonedSiteRawConfig = structuredClone(siteRawConfig);

			const hasGamUprEnabledInAnySection = Object.values(units).some(
				(section) => section?.[keyData]?.gamUprFloorAutomation?.enabled
			);

			if (!hasGamUprEnabledInAnySection && clonedSiteRawConfig?.gamUprFloorAutomation?.enabled) {
				clonedSiteRawConfig.gamUprFloorAutomation = clonedSiteRawConfig.gamUprFloorAutomation || {};
				clonedSiteRawConfig.gamUprFloorAutomation.enabled = false;
				updateSiteRawConfig(clonedSiteRawConfig);
			}
		}
	}, [gamUprFloorAutomationEnabled]);

	/**
	 * @name setGamUprDeviceFallbackConfig
	 * @param {Object} sectionRawConfig - The raw config of the section which is being updated
	 * @param {String} keyData
	 */
	const setGamUprDeviceFallbackConfig = useCallback((sectionRawConfig, keyData) => {
		GAM_UPR_PLATFORMS.forEach((device) => {
			const deviceConfig = sectionRawConfig[keyData]?.[device];
			if (!deviceConfig || Object.keys(deviceConfig).length === 0) {
				sectionRawConfig[keyData][device] = structuredClone(initialRawUnitConfig[keyData][device]);
			}
		});
	}, []);

	/**
	 * @name enableGamFloorAutomationSiteConfig
	 * @description Enables GAM floor automation at site level
	 */
	const enableGamFloorAutomationSiteConfig = () => {
		const clonedSiteRawConfig = structuredClone(siteRawConfig);
		clonedSiteRawConfig.gamUprFloorAutomation = clonedSiteRawConfig.gamUprFloorAutomation || {};

		clonedSiteRawConfig.gamUprFloorAutomation.enabled = true;
		clonedSiteRawConfig.gamUprFloorAutomation.floorAdjustmentType = 'ECPM';

		updateSiteRawConfig(clonedSiteRawConfig);
	};

	/**
	 * @name enableGamFloorAutomationSectionConfig
	 * @description Enables section-level GAM floor automation and initializes FTGUC engine
	 */
	const enableGamFloorAutomationSectionConfig = () => {
		const clonedSectionRawConfig = structuredClone(rawConfig);
		clonedSectionRawConfig[keyData] = clonedSectionRawConfig[keyData] || {};
		const clonedGamConfig = clonedSectionRawConfig[keyData];

		clonedGamConfig.gamUprFloorAutomation = clonedGamConfig.gamUprFloorAutomation || {};

		clonedGamConfig.enabled = true;
		clonedGamConfig.gamUprFloorAutomation.enabled = true;

		setGamUprDeviceFallbackConfig(clonedSectionRawConfig, keyData);

		clonedSectionRawConfig[FP_ENGINE_ENABLED_KEY] = true;
		clonedSectionRawConfig[FP_ENGINE_EXPERIMENT_KEY] = 8;

		clonedSectionRawConfig[FP_ENGINE_CONFIGS_KEY] = [FLOOR_PRICE_MODERATOR_OPTIMIZED_CONFIG];

		updateRawConfig(clonedSectionRawConfig);
	};

	/**
	 * @name disableGamFloorAutomationConfig
	 * @description Disables section-level GAM floor automation and clears FTGUC configs
	 */
	const disableGamFloorAutomationConfig = () => {
		const clonedSectionRawConfig = structuredClone(rawConfig);

		const clonedGamConfig = clonedSectionRawConfig[keyData];

		clonedGamConfig.gamUprFloorAutomation = clonedGamConfig.gamUprFloorAutomation || {};

		clonedGamConfig.gamUprFloorAutomation.enabled = false;

		clonedSectionRawConfig[FP_ENGINE_ENABLED_KEY] = false;
		clonedSectionRawConfig[FP_ENGINE_CONFIGS_KEY] = [];
		updateRawConfig(clonedSectionRawConfig);
	};

	const handleFloorAutomation = (value) => {
		if (value) {
			enableGamFloorAutomationSectionConfig();
			enableGamFloorAutomationSiteConfig();
		} else {
			disableGamFloorAutomationConfig();
		}
	};

	const tabListAndPanels = useMemo(() => {
		if (!keyData) {
			return [];
		}

		return [
			{
				tab: <Tab label={DEFAULT} value={DEFAULT} />,
				tabPanel: (
					<TabPanel value={DEFAULT}>
						<GamUprOsConfig keyData={keyData} platform={DEFAULT} />
					</TabPanel>
				)
			},
			{
				tab: <Tab label={DESKTOP} value={DESKTOP} />,
				tabPanel: (
					<TabPanel value={DESKTOP}>
						<GamUprOsConfig keyData={keyData} platform={DESKTOP} />
					</TabPanel>
				)
			},
			{
				tab: <Tab label={MOBILE} value={MOBILE} />,
				tabPanel: (
					<TabPanel value={MOBILE}>
						<GamUprOsConfig keyData={keyData} platform={MOBILE} />
					</TabPanel>
				)
			},
			{
				tab: <Tab label={TABLET} value={TABLET} />,
				tabPanel: (
					<TabPanel value={TABLET}>
						<GamUprOsConfig keyData={keyData} platform={TABLET} />
					</TabPanel>
				)
			}
		];
	}, [keyData]);

	return (
		<Box sx={{ width: '98%', typography: 'body1' }}>
			<Paper
				sx={{ paddingBottom: '6rem', margin: '3rem' }}
				className={commonStyleClasses.mainPagePaperMarginStyle}
			>
				<PageHeading
					headingName="GAM UPR"
					headingPath={['Dashboard', 'Framerate', 'Network Data', 'GAM UPR']}
					description="Manage Platform Country wise GamUpr configurations"
					showInfoButton={true}
					infoMessage={featureDescription}
				/>

				<FloorAutomationToggle
					gamUprFloorAutomationEnabled={gamUprFloorAutomationEnabled}
					onToggle={(value) => handleFloorAutomation(value)}
					infoMessage={FEATURE_DESCRIPTION.NETWORK_DATA.GAM_UPR_FLOOR_AUTOMATION}
				/>

				{!gamUprFloorAutomationEnabled && (
					<>
						<Grid
							container
							sx={{
								marginTop: '3rem',
								marginLeft: '5rem'
							}}
						>
							<Grid item>
								<CheckboxInput
									keyData={`${keyData}.enabled`}
									label="Enabled"
									defaultChecked={false}
									isObjectEditor={false}
									directValue={rawConfig[keyData]?.enabled}
									updateDirectValue={(enabledValue) => enableGamUpr(enabledValue)}
								/>
							</Grid>
						</Grid>
						<Divider
							style={{ width: '95%', margin: '0 auto', marginBottom: '2rem', marginTop: '3rem' }}
						/>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								marginTop: '5rem'
							}}
						>
							<GeneralTab
								tabListProps={{
									variant: 'fullWidth'
								}}
								tabListAndPanels={tabListAndPanels}
								tabListStyles={{ width: '62rem', marginLeft: '3rem' }}
							/>
						</Box>
					</>
				)}
			</Paper>
		</Box>
	);
};

export default GamUprPlatformConfig;
